import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { unwrapResult } from '@reduxjs/toolkit';
import TextDivider from '../../Parts/TextDivider';
import AddressModal from '../AddressModal';
import AddressSummary from '../AddressSummary';
import PaymentStep from './PaymentStep';
import Button from '../../Parts/Button';
import { QuoteStore } from '../../Store/entities/entitiesType';
import { fakeRootType } from '../../FakeRootType';
import { UsePortalDispatch } from '../../Store';
import { updateQuoteAddress } from '../../Store/entities/quotes';

type ShippingConfirmProps = {
	quote: QuoteStore;
	quoteType: string;
	setInvalidInstallationAddress: (invalid: boolean) => void;
	setInvalidShippingAddress: (invalid: boolean) => void;
	hideNext: () => void;
	showNext: () => void;
	ROOT: fakeRootType;
};

const ShippingConfirm = ({
	quote,
	quoteType,
	setInvalidInstallationAddress,
	setInvalidShippingAddress,
	hideNext,
	showNext,
	ROOT,
}: ShippingConfirmProps) => {
	const dispatch = UsePortalDispatch();
	const [editingAddress, setEditingAddress] = useState(false);
	const [addr, setAddress] = useState(quote.shipping_address);

	const requiredFieldsShippingAddress = [
		'company_name',
		'first_name',
		'last_name',
		'address',
		'country_subdivision_id',
		'city',
		'zip',
	];

	const requiredFieldsInstallationAddress = [
		'company_name',
		'address',
		'country_subdivision_id',
		'city',
		'zip',
	];

	const isShippingAddressValid = (address = addr) =>
		address && !requiredFieldsShippingAddress.some((key) =>
			address?.[key] === null || `${address?.[key]}`.length < 1);
	
	const isInstallationAddressValid = () =>
		quote.property_address
		&& !requiredFieldsInstallationAddress.some((key) =>
			quote.property_address?.[key] === null || `${quote.property_address?.[key]}`.length < 1);

	const [shippingAddressValid, setShippingAddressValid] = useState(isShippingAddressValid());
	const [installationAddressValid, setInstallationAddressValid] = useState(isInstallationAddressValid());

	useEffect(() => {
		if (!shippingAddressValid) {
			setInvalidShippingAddress(true);
			hideNext();
		}
		if (!installationAddressValid) {
			setInvalidInstallationAddress(true);
			hideNext();
		}
		if (shippingAddressValid && installationAddressValid) {
			showNext();
		}
	}, [addr]);

	return (
		<PaymentStep>
			<TextDivider>
				<h3 className={`text-${quoteType}`} style={{ fontWeight: 'normal' }}>
					Shipping Info
				</h3>
			</TextDivider>

			<div
				className="address-summary"
				style={{
					boxShadow: 'none',
					border: '1px solid gray',
					borderWidth: '0',
					borderRadius: '0',
					padding: '5px 0',
					display: 'flex',
					flexDirection: 'row',
				}}
			>
				<AddressSummary address={addr} isPropertyAddress={false} />
			</div>

			<div
				style={{
					width: '100%',
					textAlign: 'right',
					marginTop: '5px',
				}}
			>
				<Button
					color="gray"
					style={{ fontSize: '15px', marginRight: '8px' }}
					onClick={() => setEditingAddress(true)}
				>
					<FontAwesomeIcon icon={faEdit} />
					&nbsp;Edit
				</Button>
			</div>

			{editingAddress ? (
				<AddressModal
					ROOT={ROOT}
					addressLabel="Shipping Address"
					address={addr}
					onDone={() => setEditingAddress(false)}
					getAddressesFunction="getMyShippingAddresses"
					setAddress={(newAddress) => {
						dispatch(updateQuoteAddress({
							quoteId: quote.id,
							address: newAddress,
							addressType: 'shipping'
						}))
							.then(unwrapResult)
							.then((updatedQuote) => {
								setAddress(updatedQuote.shipping_address);
								setShippingAddressValid(isShippingAddressValid(updatedQuote.shipping_address));
							})
							.catch((error) => {
								console.error('Error updating shipping address:', error);
								ROOT.alert({ quickFormat: 'error', text: 'Failed to update shipping address' });
							});
					}}
					isPropertyAddress={false}
					quoteType={quoteType}
				/>
			) : null}
		</PaymentStep>
	);
};

export default ShippingConfirm;
